/* eslint-disable no-cond-assign */
import { AllowedCss, CSS_MEDIA_SIZE, CustomCssRules, ElementCss, GenericStyle } from './interface';
import { DeviceState } from '../../hooks';
import { allowedRules } from './constants';

export const getCssRules = (
  css: ElementCss[],
  selector: string,
  { isDesktop, isIE, isLandscape, isPortrait, isMobileMd, isMobileExtraSmall, isMobileSmall }: DeviceState,
): CustomCssRules | null => {
  const cssSelected = css.find((item) => item.selector === selector);
  let customCssRules: CustomCssRules | null = null;

  const addRules = (rules?: CustomCssRules): void => {
    if (rules) {
      if (customCssRules) {
        customCssRules = {
          ...customCssRules,
          ...rules,
        };
      } else {
        customCssRules = { ...rules };
      }
    }
  };

  // The repeated rules will be overwritten if added multiple times in different classes
  // isDesktop, isMobileSmall, isMobileExtraSmall and isMobileMd are mutual exclusive
  // as isLandscape and isPortrait are as well
  if (cssSelected) {
    addRules(cssSelected.default);

    if (isDesktop) {
      addRules(cssSelected.desktop);
    }

    if (isMobileSmall) {
      addRules(cssSelected.mobileSmall);
    }

    if (isMobileExtraSmall) {
      addRules(cssSelected.mobileExtraSmall);
    }

    if (isMobileMd) {
      addRules(cssSelected.mobile);
    }

    if (isLandscape && !isDesktop) {
      addRules(cssSelected.landscape);
    }

    if (isPortrait && !isDesktop) {
      addRules(cssSelected.portrait);
    }

    if (isIE) {
      addRules(cssSelected.ie);
    }
  }

  return customCssRules;
};

export const parseAllowedRules = (css: string, { directives }: AllowedCss): GenericStyle | undefined => {
  const tokenizer = /\s*([a-z-]+)\s*:\s*((?:[^;]*url\(.*?\)[^;]*|[^;]*)*)\s*(?:;|$)/gi;
  let token;
  let obj: GenericStyle | undefined;

  while ((token = tokenizer.exec(css)) !== null) {
    const directive = token[1];

    if (typeof directive !== 'string') {
      break;
    }

    if (directives.some((dir) => directive.indexOf(dir) === 0)) {
      obj = {
        ...obj,
        [directive.toLowerCase()]: token[2],
      };
    }
  }

  return obj;
};

export const getAllowedRulesBySelector = (
  selector: string,
): { allowedCss: AllowedCss; media: CSS_MEDIA_SIZE } | null => {
  const allowedCss = allowedRules.find((rule) => selector.indexOf(rule.selector) === 0);

  if (!allowedCss) {
    return null;
  }

  if (selector.indexOf(CSS_MEDIA_SIZE.DESKTOP) !== -1) {
    return {
      allowedCss,
      media: CSS_MEDIA_SIZE.DESKTOP,
    };
  }

  if (selector.indexOf(CSS_MEDIA_SIZE.IE) !== -1) {
    return {
      allowedCss,
      media: CSS_MEDIA_SIZE.IE,
    };
  }

  if (selector.indexOf(CSS_MEDIA_SIZE.LANDSCAPE) !== -1) {
    return {
      allowedCss,
      media: CSS_MEDIA_SIZE.LANDSCAPE,
    };
  }

  if (selector.indexOf(CSS_MEDIA_SIZE.MOBILE_S) !== -1) {
    return {
      allowedCss,
      media: CSS_MEDIA_SIZE.MOBILE_S,
    };
  }

  if (selector.indexOf(CSS_MEDIA_SIZE.MOBILE_XS) !== -1) {
    return {
      allowedCss,
      media: CSS_MEDIA_SIZE.MOBILE_XS,
    };
  }

  if (selector.indexOf(CSS_MEDIA_SIZE.MOBILE) !== -1) {
    return {
      allowedCss,
      media: CSS_MEDIA_SIZE.MOBILE,
    };
  }

  if (selector.indexOf(CSS_MEDIA_SIZE.PORTRAIT) !== -1) {
    return {
      allowedCss,
      media: CSS_MEDIA_SIZE.PORTRAIT,
    };
  }

  return {
    allowedCss,
    media: CSS_MEDIA_SIZE.DEFAULT,
  };
};

export const parseSelector = (value: string): string => {
  return value.trim().replace(/\s*,\s*/, ', ');
};

export const parseCss = (value: string): ElementCss[] => {
  const tokenizer = /([\s\S]+?)\{([\s\S]*?)\}/gm;
  const text = value.replace(/\/\*[\s\S]*?\*\//g, '');

  let token;
  let parsed: ElementCss[] = [];

  while ((token = tokenizer.exec(text)) !== null) {
    const selector = parseSelector(token[1]);
    const allowed = getAllowedRulesBySelector(selector);
    const rules = allowed && parseAllowedRules(token[2].trim(), allowed.allowedCss);

    if (rules && allowed?.allowedCss) {
      const parsedElement = parsed.find((el) => el.selector === allowed.allowedCss.selector);

      if (!parsedElement) {
        parsed = [
          ...parsed,
          {
            [allowed.media]: rules,
            selector: allowed.allowedCss.selector,
          },
        ];
      } else {
        parsed = parsed.map((el) => {
          if (el.selector === allowed.allowedCss.selector) {
            return {
              ...el,
              [allowed.media]: rules,
            };
          }

          return el;
        });
      }
    }
  }

  return parsed;
};
